import React, { useContext, useEffect, useRef, useState } from "react";

import GlobalContext from "shared/providers/context/GlobalContext";

import Select from "@CheckoutStepsOffers/components/atoms/Select";
import useCheckoutStepsOffersContext from "@CheckoutStepsOffers/provider";
import { useCheckoutContext } from "@payment/providers/CheckoutProvider/useCheckoutContext";
import { handleBrand } from "@payment/utils/handleBrand";
import { handleCard } from "@payment/utils/handleCard";
import { Scope, useField } from "@unform/core";

import usePaymentContext from "../../provider";
import {
  AddIcon,
  AddNewCardButton,
  CardIcon,
  Container,
  InvalidMessage,
  Label,
  RadioContainer,
  RadioContent,
  RadioInput,
  SelectContainer,
} from "./styles";

export interface IOptions {
  id: string;
  value: string;
  label: React.ReactNode;
}

const CreditCardListWithoutScope: React.FC = () => {
  const { state } = useContext(GlobalContext);
  const { creditCardState, country } = useCheckoutContext();
  const {
    installments,
    installmentOptions,
    handleSelectInstallment,
    installmentSelected,
  } = useCheckoutStepsOffersContext();
  const { setOpenCreditCardForm } = usePaymentContext();

  const inputRefs = useRef<HTMLInputElement[]>([]);

  const [checkedInput, setCheckedInput] = useState<number>(null);
  const {
    error,
    fieldName,
    clearError,
    registerField,
    defaultValue = "",
  } = useField("card_id");

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRefs,
      getValue: (refs) => {
        return refs.current.find((input) => input?.checked)?.value;
      },
      setValue: (refs, id) => {
        const inputRef = refs.current.find((ref) => ref.id === id);
        if (inputRef) inputRef.checked = true;
      },
      clearValue: (refs) => {
        const inputRef = refs.current.find((ref) => ref.checked === true);
        if (inputRef) inputRef.checked = false;
      },
    });
  }, [fieldName, registerField]);

  const onClickContainer = (ref: HTMLInputElement) => {
    ref.click();
    clearError();
  };

  useEffect(() => {
    inputRefs.current.forEach((ref, index) => {
      if (defaultValue.includes(ref.id)) setCheckedInput(index);
    });
  }, [defaultValue, inputRefs]);

  const acquirer = state?.tenancy?.acquirer;
  const IS_MIN_VALUE =
    acquirer?.complement_min_value &&
    installments[0].value < acquirer?.min_value;

  const options: IOptions[] = creditCardState.creditCardList?.map((card) => ({
    id: card.id,
    value: card.id,
    label: (
      <Label htmlFor={card.id}>
        <CardIcon src={handleBrand(card.brand, state.theme.value)} />
        {handleCard(card.brand)} {card.last_digits}
      </Label>
    ),
  }));

  useEffect(() => {
    if (country !== "Brasil") {
      handleSelectInstallment("1");
    }
  }, []);

  return (
    <Container>
      {error && <InvalidMessage>{error}</InvalidMessage>}

      {options.map((option, index) => (
        <RadioContainer
          key={option.id}
          error={!!error}
          checked={index === checkedInput}
          onClick={() => onClickContainer(inputRefs.current[index])}
        >
          <RadioContent>
            <RadioInput
              type="radio"
              id={option.id}
              name={fieldName}
              value={option.value}
              onClick={(e) => {
                setCheckedInput(index);
              }}
              ref={(ref) => (inputRefs.current[index] = ref)}
              defaultChecked={defaultValue?.includes(option.id)}
            />

            {option.label}
          </RadioContent>

          {inputRefs.current[index]?.checked &&
            !IS_MIN_VALUE &&
            country === "Brasil" && (
              <SelectContainer>
                <Select
                  required
                  name="installments"
                  options={installmentOptions}
                  defaultValue={installmentSelected.installment.toString()}
                  onChangeCustom={(option) => {
                    const value = option.value;

                    handleSelectInstallment(value);
                  }}
                />
              </SelectContainer>
            )}
        </RadioContainer>
      ))}

      <AddNewCardButton
        type="button"
        onClick={() => setOpenCreditCardForm(true)}
      >
        <AddIcon />
        Adicionar novo cartão
      </AddNewCardButton>
    </Container>
  );
};

const CreditCardList = () => {
  return (
    <Scope path="credit_card_list">
      <CreditCardListWithoutScope />
    </Scope>
  );
};

export default CreditCardList;
